import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import "./EngineeringPhilosophy.css";

// Background image
import bgImage from "../../images/bg/feature-bg.jpg";

// Principle icons

import { ListChecks, Cpu, Users, ShieldCheck } from "lucide-react";

gsap.registerPlugin(ScrollTrigger);

interface Principle {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const principles: Principle[] = [
  {
    icon: <ListChecks aria-hidden="true" />,
    title: "Engineering Discipline",
    description:
      "Structured execution that brings consistency, predictability, and quality to every engineering engagement.",
  },
  {
    icon: <Cpu aria-hidden="true" />,
    title: "Technical Excellence",
    description:
      "Specialized semiconductor engineering expertise backed by continuous learning and evolving semiconductor technologies.",
  },
  {
    icon: <Users aria-hidden="true" />,
    title: "Collaborative Ownership",
    description:
      "Working as a seamless extension of customer engineering teams through transparency, accountability, and shared goals.",
  },
  {
    icon: <ShieldCheck aria-hidden="true" />,
    title: "Reliable Outcomes",
    description:
      "Maintaining a consistent focus on delivering dependable engineering results across the semiconductor development lifecycle.",
  },
];

const EngineeringPhilosophy: React.FC = () => {
  const sectionRef = useRef<HTMLElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const section = sectionRef.current;
    const content = contentRef.current;

    if (!section || !content) return;

    const context = gsap.context(() => {
      gsap.set(section, {
        backgroundPosition: "center 0px",
        backgroundSize: "cover",
      });

      gsap.to(section, {
        backgroundPositionY: "200px",
        ease: "none",
        scrollTrigger: {
          trigger: section,
          start: "top bottom",
          end: "bottom top",
          scrub: 1,
        },
      });

      gsap.fromTo(
        content.querySelectorAll(".engineering-philosophy-animate"),
        {
          opacity: 0,
          y: 35,
        },
        {
          opacity: 1,
          y: 0,
          duration: 0.85,
          stagger: 0.12,
          ease: "power3.out",
          scrollTrigger: {
            trigger: content,
            start: "top 78%",
            once: true,
          },
        }
      );
    }, section);

    return () => {
      context.revert();
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="engineering-philosophy-section"
      style={{
        backgroundImage: `url(${bgImage})`,
      }}
    >
      <div className="engineering-philosophy-overlay" />

      <div className="container engineering-philosophy-container">
        <div ref={contentRef} className="engineering-philosophy-content">
          <div className="row align-items-start">
            <div className="col-xl-8 col-lg-9 col-md-11">
              <div className="engineering-philosophy-heading engineering-philosophy-animate">
                <span className="engineering-philosophy-subtitle">
                  Engineering Philosophy
                </span>

                <h2 className="engineering-philosophy-title">
                  Principles that shape every engineering engagement
                </h2>

                <p className="engineering-philosophy-description">
                  Every successful semiconductor program is built on more than
                  technical expertise. It requires disciplined processes,
                  collaborative execution, continuous learning, and an
                  uncompromising commitment to quality.
                </p>

                <p className="engineering-philosophy-description engineering-philosophy-description-secondary">
                  At Digicomm Semiconductor, these principles shape the way we
                  think, work, and deliver—ensuring every engineering
                  engagement contributes to reliable silicon and long-term
                  customer success.
                </p>
              </div>
            </div>
          </div>

          <div className="engineering-principles-heading engineering-philosophy-animate">
            <span className="engineering-principles-label">
              Our Engineering Principles
            </span>

            <span className="engineering-principles-line" />
          </div>

          <div className="row mt-none-30">
            {principles.map((principle, index) => (
              <div
                className="col-xl-6 col-lg-6 col-md-6 mt-30 engineering-philosophy-animate"
                key={principle.title}
              >
                <article className="engineering-principle-card">
                  <div className="engineering-principle-card-inner">
                    <div className="engineering-principle-top">
                      <span className="engineering-principle-number">
                        {String(index + 1).padStart(2, "0")}
                      </span>

                      <span className="engineering-principle-icon">
                        {principle.icon}
                      </span>
                    </div>

                    <div className="engineering-principle-content">
                      <h3 className="engineering-principle-title">
                        {principle.title}
                      </h3>

                      <p className="engineering-principle-description">
                        {principle.description}
                      </p>
                    </div>

                    <span className="engineering-principle-corner" />
                  </div>
                </article>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default EngineeringPhilosophy;